import { AuthService } from "./auth.service";

const API_BASE_URL = "/api";

export type GoogleFile = {
  id: string;
  name: string;
};

export type GoogleSheet = {
  title: string;
  sheetId: number;
};

export type GoogleSheetsConfig = {
  isConnected: boolean;
  isEnabled: boolean;
  spreadsheetId: string | null;
  spreadsheetName: string | null;
  sheetId: string | null;
  sheetName: string | null;
  exportMode: "ALL" | "ORDERS_ONLY" | "LEADS_ONLY";
  columnMapping: Record<string, string>;
  includeHeaders: boolean;
};

export type SaveConfigPayload = {
  spreadsheet_id: string;
  spreadsheet_name: string;
  sheet_id: string;
  sheet_name: string;
  export_mode: "ALL" | "ORDERS_ONLY" | "LEADS_ONLY";
  column_mapping: Record<string, string>;
  include_headers: boolean;
  is_enabled: boolean;
};

async function getHeaders(): Promise<Record<string, string>> {
  const token = await AuthService.getShopifyToken();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    "Content-Type": "application/json",
  };
}

export const GoogleSheetsService = {
  getAuthUrl: async (): Promise<string> => {
    const headers = await getHeaders();
    const res = await fetch(`${API_BASE_URL}/integrations/google/auth-url`, {
      headers,
    });
    if (!res.ok) throw new Error("Failed to get auth URL");
    const data = await res.json();
    return data.url as string;
  },

  handleCallback: async (code: string): Promise<void> => {
    const headers = await getHeaders();
    const res = await fetch(`${API_BASE_URL}/integrations/google/callback`, {
      method: "POST",
      headers,
      body: JSON.stringify({ code }),
    });
    if (!res.ok) throw new Error("Failed to exchange auth code");
  },

  getConfig: async (): Promise<GoogleSheetsConfig | null> => {
    const headers = await getHeaders();
    const res = await fetch(`${API_BASE_URL}/integrations/google/config`, {
      headers,
    });
    if (!res.ok) throw new Error("Failed to load Google Sheets config");
    return res.json() as Promise<GoogleSheetsConfig | null>;
  },

  listFiles: async (): Promise<GoogleFile[]> => {
    const headers = await getHeaders();
    const res = await fetch(`${API_BASE_URL}/integrations/google/files`, {
      headers,
    });
    if (!res.ok) return [];
    const data = await res.json();
    return (data as { id?: string; name?: string }[]).map((f) => ({
      id: f.id ?? "",
      name: f.name ?? "",
    }));
  },

  listSheets: async (fileId: string): Promise<GoogleSheet[]> => {
    const headers = await getHeaders();
    const res = await fetch(
      `${API_BASE_URL}/integrations/google/files/${encodeURIComponent(fileId)}/sheets`,
      { headers }
    );
    if (!res.ok) return [];
    const data = await res.json();
    return (data as { title?: string; sheetId?: number }[]).map((s) => ({
      title: s.title ?? "",
      sheetId: s.sheetId ?? 0,
    }));
  },

  getAvailableFields: async (): Promise<string[]> => {
    const headers = await getHeaders();
    const res = await fetch(`${API_BASE_URL}/integrations/google/fields`, {
      headers,
    });
    if (!res.ok) return [];
    const data = await res.json();
    return (data as { fields: string[] }).fields ?? [];
  },

  saveConfig: async (payload: SaveConfigPayload): Promise<void> => {
    const headers = await getHeaders();
    const res = await fetch(`${API_BASE_URL}/integrations/google/config`, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error("Failed to save Google Sheets config");
  },

  disconnect: async (): Promise<void> => {
    const headers = await getHeaders();
    delete headers["Content-Type"];
    const res = await fetch(`${API_BASE_URL}/integrations/google/disconnect`, {
      method: "DELETE",
      headers,
    });
    if (!res.ok) throw new Error("Failed to disconnect Google account");
  },
};
